import {
  DialogActionTrigger,
  DialogBody,
  DialogContent,
  DialogCloseTrigger,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@chakra-ui/react';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface ConfirmDialogProps {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Dialog title */
  title: string;
  /** Description text or custom JSX content */
  description: string | React.ReactElement;
  /** Callback when confirm button is clicked */
  onConfirm: () => void;
  /** Callback when cancel button is clicked or dialog is closed */
  onCancel: () => void;
  /** Custom text for confirm button (defaults to common.confirm) */
  confirmText?: string;
  /** Custom text for cancel button (defaults to common.cancel) */
  cancelText?: string;
  /** Color palette for confirm button (defaults to primary) */
  confirmColorPalette?: 'red' | 'blue' | 'green' | 'gray' | 'primary';
  /** Variant for confirm button (defaults to solid) */
  confirmVariant?: 'solid' | 'outline' | 'ghost' | 'plain';
  /** Whether the confirm action is loading */
  isLoading?: boolean;
  /** Blocks confirmation while the dialog's own content is incomplete or invalid */
  isConfirmDisabled?: boolean;
  /** Whether to close on interact outside (defaults to false) */
  closeOnInteractOutside?: boolean;
  /** Whether to close on escape key (defaults to true) */
  closeOnEscape?: boolean;
  /** Max width of dialog (defaults to md) */
  maxW?: string;
  /** Reverse button order - puts confirm button first (left) instead of last (right) */
  reverseButtons?: boolean;
  /** Return focus to a stable action trigger when the menu item has unmounted. */
  finalFocusEl?: () => HTMLElement | null;
  /** Adds the shared close button for settings confirmations. */
  showCloseTrigger?: boolean;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  title,
  description,
  onConfirm,
  onCancel,
  confirmText,
  cancelText,
  confirmColorPalette = 'gray',
  confirmVariant = 'solid',
  isLoading = false,
  isConfirmDisabled = false,
  closeOnInteractOutside = false,
  closeOnEscape = true,
  maxW = 'md',
  reverseButtons = false,
  showCloseTrigger = false,
  finalFocusEl,
}) => {
  const { t } = useTranslation();

  const handleCancel = () => {
    if (!isLoading) {
      onCancel();
    }
  };

  const handleOpenChange = () => {
    // onOpenChange is called by Chakra UI when the dialog wants to close
    handleCancel();
  };

  const cancelButton = (
    <DialogActionTrigger asChild>
      <Button variant="outline" onClick={handleCancel} disabled={isLoading}>
        {cancelText || t('common.cancel')}
      </Button>
    </DialogActionTrigger>
  );

  const confirmButton = (
    <Button
      colorPalette={confirmColorPalette}
      variant={confirmVariant}
      onClick={onConfirm}
      loading={isLoading}
      disabled={isConfirmDisabled}
    >
      {confirmText || t('common.confirm')}
    </Button>
  );

  return (
    <DialogRoot
      open={isOpen}
      onOpenChange={handleOpenChange}
      closeOnInteractOutside={closeOnInteractOutside}
      closeOnEscape={closeOnEscape}
      finalFocusEl={finalFocusEl}
      placement="center"
    >
      <DialogContent rounded="2xl" maxW={maxW}>
        {showCloseTrigger && (
          <DialogCloseTrigger disabled={isLoading} aria-label={t('common.close')} />
        )}
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <DialogDescription>{description}</DialogDescription>
        </DialogBody>
        <DialogFooter>
          {reverseButtons ? (
            <>
              {confirmButton}
              {cancelButton}
            </>
          ) : (
            <>
              {cancelButton}
              {confirmButton}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
};

export { ConfirmDialog };
